/**
 * useServices - Service catalog hook
 *
 * The 7 services merged by slug: server price, duration, currency and
 * bookable_online (GET /api/public/services) over the static marketing
 * copy in data/services.js. Cached for 5 minutes across components.
 *
 * Until the API answers (or if it fails) `services` holds the static
 * catalog, so pages render immediately; `source` says which one is shown.
 *
 * @example
 * const { bookableServices, loading, error } = useServices();
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getServiceCatalog,
  getCachedServiceCatalog,
  getStaticServiceCatalog,
} from '../services/api/servicesCatalog';
import { getErrorMessage } from '../services/errors/errorHandler';

const initialState = () => {
  const cached = getCachedServiceCatalog();
  return cached
    ? { services: cached, source: 'api', loading: false, error: null }
    : { services: getStaticServiceCatalog(), source: 'static', loading: true, error: null };
};

/**
 * @returns {{services: Array, bookableServices: Array, getService: Function, source: 'api'|'static', loading: boolean, error: string|null, refetch: Function}}
 */
const useServices = () => {
  const [state, setState] = useState(initialState);
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    let ignore = false;
    const force = reloadCount > 0;

    if (!force && getCachedServiceCatalog()) {
      setState({ services: getCachedServiceCatalog(), source: 'api', loading: false, error: null });
      return undefined;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));
    getServiceCatalog({ force }).then(
      (services) => {
        if (!ignore) setState({ services, source: 'api', loading: false, error: null });
      },
      (err) => {
        if (!ignore) {
          setState({
            services: getStaticServiceCatalog(),
            source: 'static',
            loading: false,
            error: getErrorMessage(err),
          });
        }
      }
    );

    return () => {
      ignore = true;
    };
  }, [reloadCount]);

  const refetch = useCallback(() => setReloadCount((n) => n + 1), []);

  const bookableServices = useMemo(
    () => state.services.filter((s) => s.bookableOnline),
    [state.services]
  );

  const getService = useCallback(
    (slug) => state.services.find((s) => s.slug === slug) || null,
    [state.services]
  );

  return {
    services: state.services,
    bookableServices,
    getService,
    source: state.source,
    loading: state.loading,
    error: state.error,
    refetch,
  };
};

export default useServices;
